'use client';
import React from 'react';
import { t } from '@utils/translate';
import { renderTranslation } from '@utils/renderTranslation';
import { LogoRotator } from '@ui';
import { Section, Container, SectionGroup } from '@layout';
import HomeProjectCard, {
  ctaLabelForProject,
  type HomeProjectEntry,
} from './projects/HomeProjectCard';

interface ProjectsWrapperProps {
  sectionID?: string;
  className?: string;
  classes?: string;
  isSpanish?: boolean;
}

const ProjectsWrapper: React.FC<ProjectsWrapperProps> = ({
  isSpanish = false,
  sectionID,
  className,
  classes = '',
}) => {
  const locale = isSpanish ? 'es' : 'en';
  const projectsRaw = t('projects', 'home', locale);
  const ns = 'home';
  const projects = (Array.isArray(projectsRaw) ? (projectsRaw as HomeProjectEntry[]) : []).filter(
    (project) => project.hidden !== true
  );
  const featuredIndex = projects.findIndex((p) => p.isFeatured === true);
  const featuredProject = featuredIndex >= 0 ? projects[featuredIndex] : projects[0];
  const gridProjects =
    featuredIndex >= 0 ? projects.filter((_, i) => i !== featuredIndex) : projects.slice(1);
  const ctaRaw = renderTranslation(t('projects_cta', ns, locale));
  const ctaLabel =
    ctaRaw != null && ctaRaw !== ''
      ? String(ctaRaw)
      : locale === 'es'
        ? 'Ver proyecto'
        : 'View project';
  const toSectionText = (raw: string | number | boolean | null) =>
    raw == null || raw === '' ? '' : String(raw);
  const projectsEyebrow = toSectionText(renderTranslation(t('projects_eyebrow', ns, locale)));
  const projectsHeadingHint = toSectionText(
    renderTranslation(t('projects_heading_hint', ns, locale))
  );
  const projectsTitle = toSectionText(renderTranslation(t('projects_heading', ns, locale)));
  const projectsDescription = toSectionText(renderTranslation(t('projects_blurb', ns, locale)));
  const sectionClass = ['home-projects', classes, className].filter(Boolean).join(' ').trim();

  return (
    <>
      <SectionGroup
        groupId={sectionID ?? 'home-projects'}
        groupClassName='section-group flush'
        eyebrow={projectsEyebrow}
        title={projectsTitle}
        headingHint={projectsHeadingHint}
        as='h2'
        description={projectsDescription}
      >
        <Section sectionID={sectionID} className={sectionClass}>
          {projects.length > 0 && featuredProject ? (
            <>
              <Container>
                <HomeProjectCard
                  project={featuredProject}
                  featured
                  ctaLabel={ctaLabelForProject(featuredProject, ctaLabel)}
                />
              </Container>
              {gridProjects.length > 0 ? (
                <Container>
                  <div className='project-grid'>
                    {gridProjects.map((project, idx) => (
                      <HomeProjectCard
                        key={project.id ?? project.project_id ?? idx + 1}
                        project={project}
                        ctaLabel={ctaLabelForProject(project, ctaLabel)}
                      />
                    ))}
                  </div>
                </Container>
              ) : null}
            </>
          ) : (
            <Container>
              <div style={{ color: 'red', padding: '1rem' }}>
                No projects found for locale: {locale}
              </div>
            </Container>
          )}
        </Section>
        <LogoRotator className='' surface='sf-1' locale={locale} />
      </SectionGroup>
    </>
  );
};

export default ProjectsWrapper;
